import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { Check, ExternalLink, Loader2, Plus, SlidersHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { correctionApi, type CorrectionRule, type CorrectionScope, type CorrectionStatus, type CorrectionStrength } from '@/api/correctionApi'
import { friendlyMessage } from '@/api/http'
import { queryClient } from '@/app/queryClient'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

const scopeOptions: Array<{ value: CorrectionScope; label: string }> = [
  { value: 'book', label: '全书' },
  { value: 'character', label: '角色' },
  { value: 'style', label: '风格' },
  { value: 'redline', label: '红线' },
]

const statusOptions: Array<{ value: CorrectionStatus | 'all'; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'proposed', label: '待确认' },
  { value: 'confirmed', label: '已启用' },
  { value: 'discarded', label: '已停用' },
]

function sourceChapterID(rule: CorrectionRule): string {
  const value = rule.source?.chapter_id
  return typeof value === 'string' ? value : ''
}

function scopeLabel(scope: string): string {
  return scopeOptions.find((item) => item.value === scope)?.label ?? scope
}

function statusLabel(status: string): string {
  return statusOptions.find((item) => item.value === status)?.label ?? status
}

export default function ProjectCorrections() {
  const { id } = useParams()
  const projectId = id ?? ''
  const [status, setStatus] = useState<CorrectionStatus | 'all'>('all')
  const [scope, setScope] = useState<CorrectionScope>('style')
  const [strength, setStrength] = useState<CorrectionStrength>('soft')
  const [tag, setTag] = useState('')
  const [ruleText, setRuleText] = useState('')
  const [reason, setReason] = useState('')

  const queryKey = ['corrections', projectId, status]
  const rulesQuery = useQuery({
    queryKey,
    queryFn: () => correctionApi.list(projectId, status === 'all' ? undefined : status),
    enabled: Boolean(projectId),
  })

  const filteredRules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data])

  const createMutation = useMutation({
    mutationFn: () => correctionApi.create(projectId, {
      scope,
      tag: tag.trim() || undefined,
      rule_text: ruleText.trim(),
      reason: reason.trim() || undefined,
      strength,
      source: {},
    }),
    onSuccess: async () => {
      setRuleText('')
      setReason('')
      await queryClient.invalidateQueries({ queryKey: ['corrections', projectId] })
      toast.success('纠偏规则已创建')
    },
    onError: (err) => toast.error(friendlyMessage(err, '创建失败')),
  })

  const confirmMutation = useMutation({
    mutationFn: (ruleId: string) => correctionApi.confirm(projectId, ruleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['corrections', projectId] })
      toast.success('纠偏规则已启用')
    },
    onError: (err) => toast.error(friendlyMessage(err, '启用失败')),
  })

  const disableMutation = useMutation({
    mutationFn: (ruleId: string) => correctionApi.disable(projectId, ruleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['corrections', projectId] })
      toast.success('纠偏规则已停用')
    },
    onError: (err) => toast.error(friendlyMessage(err, '停用失败')),
  })

  const busy = createMutation.isPending || confirmMutation.isPending || disableMutation.isPending

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">纠偏规则</h1>
          <p className="text-sm text-muted-foreground">项目级生成策略库，规则只在可转换为目标工位约束时注入。</p>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/projects/${encodeURIComponent(projectId)}`}>
            <ExternalLink />
            项目详情
          </Link>
        </Button>
      </div>

      <section className="grid gap-4 rounded-md border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Plus className="size-4" />
          新建规则
        </div>
        <div className="grid gap-4 md:grid-cols-[160px_160px_1fr]">
          <div className="space-y-2">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={(value) => setScope(value as CorrectionScope)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {scopeOptions.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>强度</Label>
            <Select value={strength} onValueChange={(value) => setStrength(value as CorrectionStrength)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="soft">Soft</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>标签</Label>
            <Input value={tag} onChange={(event) => setTag(event.target.value)} placeholder="角色名、关键词或红线词" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>规则</Label>
            <textarea
              className="min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              value={ruleText}
              onChange={(event) => setRuleText(event.target.value)}
              placeholder="例如：不要把紧张场面写成设定说明。"
            />
          </div>
          <div className="space-y-2">
            <Label>来源说明</Label>
            <textarea
              className="min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="例如：用户在第 7 章删掉了大段解释。"
            />
          </div>
        </div>
        <div>
          <Button disabled={!ruleText.trim() || busy} onClick={() => createMutation.mutate()}>
            {createMutation.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            创建待确认规则
          </Button>
        </div>
      </section>

      <section className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <SlidersHorizontal className="size-4" />
            规则列表
          </div>
          <Select value={status} onValueChange={(value) => setStatus(value as CorrectionStatus | 'all')}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>范围</TableHead>
                <TableHead>规则</TableHead>
                <TableHead>强度</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>来源章节</TableHead>
                <TableHead className="w-[150px] text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rulesQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">加载中</TableCell></TableRow>
              ) : filteredRules.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">暂无规则</TableCell></TableRow>
              ) : filteredRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span>{scopeLabel(rule.scope)}</span>
                      {rule.tag ? <span className="text-xs text-muted-foreground">{rule.tag}</span> : null}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[420px] whitespace-pre-wrap text-sm">{rule.rule_text}</TableCell>
                  <TableCell><Badge variant={rule.strength === 'hard' ? 'destructive' : 'secondary'}>{rule.strength}</Badge></TableCell>
                  <TableCell>{statusLabel(rule.status)}</TableCell>
                  <TableCell>
                    {sourceChapterID(rule) ? (
                      <Link className="text-sm text-primary underline-offset-4 hover:underline" to={`/projects/${encodeURIComponent(projectId)}/reader`}>
                        {sourceChapterID(rule)}
                      </Link>
                    ) : <span className="text-sm text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {rule.status === 'proposed' ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => confirmMutation.mutate(rule.id)}>
                          <Check />
                          确认
                        </Button>
                      ) : null}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.status === 'confirmed'}
                          disabled={busy || rule.status === 'discarded'}
                          onCheckedChange={(checked) => {
                            if (checked) confirmMutation.mutate(rule.id)
                            else disableMutation.mutate(rule.id)
                          }}
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
      <Separator />
    </div>
  )
}
